import {useNavigate} from 'react-router-dom'
import {InlineRow} from '@/components/shared/InlineRow'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {Trash2, FileText, FolderInput, MoreHorizontal, Clock} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Icon} from '@/components/shared/Icon'
import {IconText} from '@/components/shared/IconText'
import {DragHandle} from '@/components/shared/DragHandle'
import {IndentedTableCell} from '@/components/shared/IndentedTableCell'
import {SortableTableRow} from '@/components/shared/SortableTableRow'
import {TreeNodeDescription} from '@/components/shared/TreeNodeDescription'
import {formatDate} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {TableCell} from '@/components/ui/table'
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type {ReportDefinitionListItem} from './types'

export function ReportRow({report, depth, canModify, onDelete, onMove, isDeleting, sortable = false}: {
    report: ReportDefinitionListItem
    depth: number
    canModify: boolean
    onDelete: () => void
    onMove: () => void
    isDeleting: boolean
    sortable?: boolean
}) {
    const i18n = useI18n()
    const navigate = useNavigate()
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: report.reportDefinitionId,
        /* eslint-disable-next-line i18next/no-literal-string -- DnD item type identifier */
        data: {type: 'report', categoryId: report.categoryId, version: report.version},
        disabled: !sortable,
    })

    const style = sortable ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    } : undefined

    return (
        <SortableTableRow
            ref={setNodeRef}
            dragStyle={style}
            clickable
            alternateBg={depth % 2 !== 0}
            onClick={() => navigate(routes.reporting.reports.detail(report.reportDefinitionId))}
            data-testid={`report-row-${report.reportDefinitionId}`}
        >
            <IndentedTableCell
                depth={depth}
                sortable={sortable}
                tone="subtle"
            >
                <InlineRow>
                    {sortable && (
                        <DragHandle
                            {...attributes}
                            {...listeners}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`drag-report-${report.reportDefinitionId}`}
                        />
                    )}
                    {!sortable && <SmallText className="w-3.5 shrink-0">{' '}</SmallText>}
                    <Icon icon={FileText} size="sm" muted/>
                    <Strong truncate>{report.name}</Strong>
                </InlineRow>
                {report.description && (
                    <TreeNodeDescription>{report.description}</TreeNodeDescription>
                )}
            </IndentedTableCell>
            <TableCell/>
            <TableCell className="text-right">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button data-testid={`btn-actions-report-${report.reportDefinitionId}`} variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                            <Icon icon={MoreHorizontal}/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                            <IconText icon={Clock} size="xs">{report.lastRunAt ? formatDate(report.lastRunAt) : i18n.reporting.reportDetail.neverRun}</IconText>
                        </DropdownMenuItem>
                        {canModify && (
                            <>
                                <DropdownMenuSeparator/>
                                <DropdownMenuItem data-testid={`btn-move-report-${report.reportDefinitionId}`} onClick={onMove}>
                                    <Icon icon={FolderInput} className="mr-2"/>{i18n.reporting.moveReportDialog.title}
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`btn-delete-report-${report.reportDefinitionId}`} className="text-destructive" onClick={onDelete} disabled={isDeleting}>
                                    <Icon icon={Trash2} className="mr-2"/>{i18n.common.delete}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </TableCell>
        </SortableTableRow>
    )
}
